/**
 * WhatsAppMockPhone.jsx (simulator)
 * Mock de móvil con burbuja entrante + CTA al estilo WhatsApp.
 * Reutilizable para previews de campañas automáticas en el simulador.
 *
 * Props opcionales (aditivas, los callers de una sola burbuja no cambian):
 *   bubbles  — lista de { message, time, cta, secondaryCta, tertiaryCta, footer,
 *              imageHeader, outgoing } para conversaciones de varias burbujas.
 *              Si se pasa, ignora `message` y las props sueltas de burbuja.
 *   overlay  — nodo renderizado sobre el chat: un Flow de WhatsApp se abre así.
 *   footer   — línea gris bajo el texto (el pie de las plantillas de Meta).
 *   height   — alto de la pantalla; admite CSS var para previews responsive.
 */
import { useTranslation } from "react-i18next";
import React from "react";

// Formatea texto al estilo WhatsApp: *negrita*, _cursiva_, ~tachado~.
// Aplicado por línea para preservar saltos de línea con whitespace-pre-line.
function renderWA(text) {
  if (!text) return null;
  const lines = String(text).split("\n");
  return lines.flatMap((line, lineIdx) => {
    const parts = line.split(/(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g);
    const rendered = parts.map((p, i) => {
      if (/^\*[^*\n]+\*$/.test(p))
        return <strong key={`${lineIdx}-${i}`}>{p.slice(1, -1)}</strong>;
      if (/^_[^_\n]+_$/.test(p))
        return <em key={`${lineIdx}-${i}`}>{p.slice(1, -1)}</em>;
      if (/^~[^~\n]+~$/.test(p))
        return <s key={`${lineIdx}-${i}`}>{p.slice(1, -1)}</s>;
      return <React.Fragment key={`${lineIdx}-${i}`}>{p}</React.Fragment>;
    });
    return lineIdx < lines.length - 1
      ? [...rendered, <br key={`br-${lineIdx}`} />]
      : rendered;
  });
}

const WHATSAPP_WALLPAPER =
  "url('data:image/svg+xml,%3Csvg width%3D%2260%22 height%3D%2260%22 viewBox%3D%220 0 60 60%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg fill%3D%22none%22 fill-rule%3D%22evenodd%22%3E%3Cg fill%3D%22%23c9b8a0%22 fill-opacity%3D%220.18%22%3E%3Cpath d%3D%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4h-4zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2FSvg%3E') #ECE5DD";

// Una burbuja del chat. Extraída para poder pintar varias (prop `bubbles`);
// con una sola burbuja el resultado es idéntico al de antes.
function Bubble({
  message,
  time,
  cta = null,
  onCtaClick = null,
  secondaryCta = null,
  tertiaryCta = null,
  footer = null,
  imageHeader = null,
  outgoing = false,
  // Respeta los espacios del texto (sangrado de 3 de la 2.ª línea de opciones de plato en la
  // confirmación del Flow). Por burbuja y apagado por defecto: el resto de vistas previas igual.
  preserveSpaces = false,
}) {
  return (
    <div className={`${outgoing ? "self-end" : "self-start"} max-w-[88%]`}>
      <div
        className="shadow-sm overflow-hidden"
        style={{
          borderRadius: outgoing ? "12px 3px 12px 12px" : "3px 12px 12px 12px",
          background: outgoing ? "#d9fdd3" : "#fff",
        }}
      >
        {imageHeader && (
          <div className="p-1 pb-0">
            <div className="rounded-[8px] overflow-hidden">{imageHeader}</div>
          </div>
        )}
        <div className="px-3 py-2">
          <p className={`text-[12.5px] leading-snug text-gray-900 break-words [overflow-wrap:anywhere]${preserveSpaces ? " whitespace-pre-wrap" : ""}`}>
            {renderWA(message)}
          </p>
          {footer && <p className="text-[10.5px] text-gray-400 mt-1.5">{footer}</p>}
          <div className="flex justify-end mt-1">
            <span className="text-[10px] text-gray-400">{time}</span>
          </div>
        </div>
        {cta && (
          <button
            type="button"
            onClick={onCtaClick || undefined}
            className="w-full border-t border-gray-200 px-3 py-2 flex items-center justify-center gap-1.5 text-[#00A884] text-[12.5px] font-semibold hover:bg-gray-50"
          >
            {cta}
          </button>
        )}
        {secondaryCta && (
          <button
            type="button"
            className="w-full border-t border-gray-200 px-3 py-2 flex items-center justify-center gap-1.5 text-[#00A884] text-[12.5px] font-semibold hover:bg-gray-50"
          >
            {secondaryCta}
          </button>
        )}
        {tertiaryCta && (
          <button
            type="button"
            className="w-full border-t border-gray-200 px-3 py-2 flex items-center justify-center gap-1.5 text-[#00A884] text-[12.5px] font-semibold hover:bg-gray-50"
          >
            {tertiaryCta}
          </button>
        )}
      </div>
    </div>
  );
}

export default function WhatsAppMockPhone({
  senderName = "Tu negocio",
  senderAvatar = "💼",
  senderAvatarImg = null,
  message = "",
  time = "10:24",
  cta = null,
  onCtaClick = null,
  secondaryCta = null,
  tertiaryCta = null,
  imageHeader = null,
  footer = null,
  // outgoing: burbuja saliente (del negocio) → alineada a la derecha y en verde
  // WhatsApp. Por defecto false (burbuja entrante, izquierda, blanca) para no
  // afectar a las previews de campañas que la usan.
  outgoing = false,
  bubbles = null,
  overlay = null,
  height = 520,
}) {
  const { t } = useTranslation("extra_1");
  const list =
    bubbles && bubbles.length > 0
      ? bubbles
      : [{ message, time, cta, onCtaClick, secondaryCta, tertiaryCta, footer, imageHeader, outgoing }];
  return (
    <div
      className="relative rounded-[40px] shadow-2xl"
      style={{
        width: 280,
        background: "#18181b",
        padding: 8,
        border: "2px solid #3f3f46",
      }}
    >
      <div
        className="absolute left-1/2 -translate-x-1/2 bg-[#18181b] rounded-full z-10"
        style={{ top: 10, width: 60, height: 16 }}
      />
      <div
        className="rounded-[32px] overflow-hidden flex flex-col"
        style={{ height, background: "#ECE5DD" }}
      >
        <div className="bg-white border-b border-gray-200 px-3 py-2 pt-6 flex items-center gap-2.5 flex-shrink-0">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6b7280"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center text-base flex-shrink-0 overflow-hidden">
            {senderAvatarImg ? (
              <img
                src={senderAvatarImg}
                alt={senderName}
                className="w-full h-full object-cover"
              />
            ) : (
              senderAvatar
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-gray-900 text-[13px] font-semibold leading-tight truncate">
              {senderName}
            </p>
            <p className="text-[10px] text-gray-500">{t("whatsAppMockPhone.online", "en línea")}</p>
          </div>
        </div>
        <div className="flex-1 relative min-h-0">
          <div
            className="absolute inset-0 overflow-y-auto px-3 py-3 flex flex-col gap-2"
            style={{ background: WHATSAPP_WALLPAPER }}
          >
            {list.map((b, i) => (
              <Bubble key={i} {...b} />
            ))}
          </div>
          {overlay}
        </div>
        <div className="bg-[#F0F0F0] px-2.5 py-2 flex items-center gap-2 flex-shrink-0">
          <div className="w-7 h-7 flex items-center justify-center text-gray-500 text-lg">
            +
          </div>
          <div
            className="flex-1 bg-white rounded-full px-3 py-1.5 text-gray-400"
            style={{ fontSize: 11 }}
          />
        </div>
      </div>
    </div>
  );
}
